import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { toast } from "react-toastify";
import { FiDollarSign, FiSearch, FiX, FiCreditCard, FiClock, FiCheckCircle, FiPercent } from "react-icons/fi";
import ExportButton from "./ExportButton";
import {
    Button,
    Card,
    PageHeader,
    StatCard,
    StatusBadge,
    DataTable,
    Select,
    EmptyState,
    TableSkeleton,
    useConfirm,
    formatDateTime,
} from "./ui";

export default function PembayaranPanel() {
  const confirm = useConfirm();

  const [pembayaranList, setPembayaranList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [filterKodeToko, setFilterKodeToko] = useState("");
  const [filterReferal, setFilterReferal] = useState("");
  const [filterKeterangan, setFilterKeterangan] = useState("");
  const [filterBulan, setFilterBulan] = useState("");

  useEffect(() => {
    fetchPembayaran();
  }, []);

  useEffect(() => {
    filterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pembayaranList, search, filterKodeToko, filterReferal, filterKeterangan, filterBulan]);

  const fetchPembayaran = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("pembayaran")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      setError(error.message);
      setPembayaranList([]);
    } else {
      setPembayaranList(data);
      setError(null);
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: "Hapus Pembayaran",
      message: "Apakah Anda yakin ingin menghapus pembayaran pending ini?",
      confirmLabel: "Ya, Hapus",
      tone: "danger",
    });
    if (!ok) return;

    setLoading(true);
    const { error } = await supabase
      .from("pembayaran")
      .delete()
      .eq("id", id);

    if (error) {
      toast.error("Gagal menghapus: " + error.message);
      setLoading(false);
    } else {
      fetchPembayaran();
    }
  };

  const getUniqueValues = (key) => {
    return [...new Set(pembayaranList.map((item) => item[key]).filter(Boolean))];
  };

  const getUniqueMonths = () => {
    const bulanSet = new Set(
      pembayaranList.map((item) => new Date(item.created_at).getMonth() + 1)
    );
    return Array.from(bulanSet).sort((a, b) => a - b);
  };

  const filterData = () => {
    const keyword = search.trim().toLowerCase();
    const filtered = pembayaranList.filter((item) => {
      const tanggal = new Date(item.created_at);
      const matchKodeToko = filterKodeToko ? item.kode_toko === filterKodeToko : true;
      const matchReferal = filterReferal ? item.referal === filterReferal : true;
      const matchKeterangan = filterKeterangan ? item.keterangan === filterKeterangan : true;
      const matchBulan = filterBulan ? tanggal.getMonth() + 1 === parseInt(filterBulan) : true;
      const matchSearch = keyword
        ? String(item.kode_toko || "").toLowerCase().includes(keyword) ||
          String(item.referal || "").toLowerCase().includes(keyword)
        : true;
      return matchKodeToko && matchReferal && matchKeterangan && matchBulan && matchSearch;
    });
    setFilteredList(filtered);
  };

  const clearFilters = () => {
    setSearch("");
    setFilterKodeToko("");
    setFilterReferal("");
    setFilterKeterangan("");
    setFilterBulan("");
  };

  const pembayaranBerhasil = filteredList.filter((item) => item.keterangan?.toLowerCase() === "berhasil");
  const totalJumlah = pembayaranBerhasil.reduce((sum, item) => sum + (item.amount || 0), 0);
  const totalMargin = pembayaranBerhasil.reduce((sum, item) => sum + (item.margin_max || 0), 0);
  const pendingCount = filteredList.filter((item) => item.keterangan?.toLowerCase() === "pending").length;
  const hasActiveFilters = search || filterKodeToko || filterReferal || filterKeterangan || filterBulan;

  const columns = [
    {
      key: "kode_toko",
      header: "Kode Toko",
      cell: (p) => <span className="font-semibold text-slate-900 dark:text-slate-100">{p.kode_toko || "-"}</span>,
    },
    {
      key: "amount",
      header: "Jumlah",
      cell: (p) => <span className="text-slate-700 dark:text-slate-300">Rp {(p.amount || 0).toLocaleString("id-ID")}</span>,
    },
    {
      key: "keterangan",
      header: "Status",
      cell: (p) => <StatusBadge status={p.keterangan} size="sm" />,
    },
    {
      key: "referal",
      header: "Referal",
      hideBelow: "lg",
      cell: (p) => <span className="text-slate-600 dark:text-slate-400">{p.referal || "-"}</span>,
    },
    {
      key: "margin_max",
      header: "Margin",
      hideBelow: "xl",
      cell: (p) => <span className="text-slate-600 dark:text-slate-400">{p.margin_max ? `Rp ${p.margin_max.toLocaleString("id-ID")}` : "-"}</span>,
    },
    {
      key: "created_at",
      header: "Tanggal",
      hideBelow: "lg",
      cell: (p) => <span className="text-sm text-slate-500 dark:text-slate-400">{formatDateTime(p.created_at)}</span>,
    },
    {
      key: "aksi",
      header: "Aksi",
      headerClassName: "text-right",
      className: "text-right",
      cell: (p) =>
        p.keterangan?.toLowerCase() === "pending" ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDelete(p.id)}
            className="text-danger-600 hover:bg-danger-50 dark:text-danger-400 dark:hover:bg-danger-500/10"
          >
            Hapus
          </Button>
        ) : (
          <span className="text-xs text-slate-300 dark:text-slate-600">—</span>
        ),
    },
  ];

  if (error) {
    return (
      <Card className="border-danger-200 bg-danger-50 p-4 text-sm text-danger-700 dark:border-danger-500/20 dark:bg-danger-500/10 dark:text-danger-300">
        {error}
      </Card>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Pembayaran"
        subtitle="Monitor seluruh transaksi pembayaran"
        icon={FiDollarSign}
        actions={<ExportButton data={filteredList} filename="pembayaran" />}
      />

      {/* Summary */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <StatCard label="Total Transaksi" value={filteredList.length} icon={FiCreditCard} tone="primary" />
        <StatCard label="Revenue" value={`Rp ${totalJumlah.toLocaleString("id-ID")}`} icon={FiDollarSign} tone="success" />
        <StatCard label="Pending" value={pendingCount} icon={FiClock} tone="warning" />
        <StatCard label="Berhasil" value={pembayaranBerhasil.length} icon={FiCheckCircle} tone="success" />
        <StatCard label="Total Margin" value={`Rp ${totalMargin.toLocaleString("id-ID")}`} icon={FiPercent} tone="secondary" />
      </div>

      <Card>
        {/* Filter toolbar */}
        <div className="border-b border-slate-100 p-4 dark:border-slate-800">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-6">
            <div className="relative sm:col-span-2">
              <FiSearch className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cari kode toko / referal..."
                className="field-base pl-10"
              />
            </div>
            <Select value={filterKodeToko} onChange={(e) => setFilterKodeToko(e.target.value)} aria-label="Filter kode toko">
              <option value="">Semua Kode Toko</option>
              {getUniqueValues("kode_toko").map((kode) => (
                <option key={kode} value={kode}>{kode}</option>
              ))}
            </Select>
            <Select value={filterReferal} onChange={(e) => setFilterReferal(e.target.value)} aria-label="Filter referal">
              <option value="">Semua Referal</option>
              {getUniqueValues("referal").map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </Select>
            <Select value={filterKeterangan} onChange={(e) => setFilterKeterangan(e.target.value)} aria-label="Filter status">
              <option value="">Semua Status</option>
              {getUniqueValues("keterangan").map((k) => (
                <option key={k} value={k}>{k}</option>
              ))}
            </Select>
            <Select value={filterBulan} onChange={(e) => setFilterBulan(e.target.value)} aria-label="Filter bulan">
              <option value="">Semua Bulan</option>
              {getUniqueMonths().map((bulan) => (
                <option key={bulan} value={String(bulan)}>Bulan {bulan}</option>
              ))}
            </Select>
          </div>

          {hasActiveFilters && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-xs text-slate-500 dark:text-slate-400">Filter aktif:</span>
              {filterKodeToko && (
                <button onClick={() => setFilterKodeToko("")} className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700 dark:bg-primary-500/10 dark:text-primary-300">
                  Kode: {filterKodeToko} <FiX className="h-3 w-3" />
                </button>
              )}
              {filterReferal && (
                <button onClick={() => setFilterReferal("")} className="inline-flex items-center gap-1 rounded-full bg-secondary-50 px-3 py-1 text-xs font-medium text-secondary-700 dark:bg-secondary-500/10 dark:text-secondary-300">
                  Referal: {filterReferal} <FiX className="h-3 w-3" />
                </button>
              )}
              {filterKeterangan && (
                <button onClick={() => setFilterKeterangan("")} className="inline-flex items-center gap-1 rounded-full bg-success-50 px-3 py-1 text-xs font-medium text-success-700 dark:bg-success-500/10 dark:text-success-300">
                  Status: {filterKeterangan} <FiX className="h-3 w-3" />
                </button>
              )}
              {filterBulan && (
                <button onClick={() => setFilterBulan("")} className="inline-flex items-center gap-1 rounded-full bg-warning-50 px-3 py-1 text-xs font-medium text-warning-700 dark:bg-warning-500/10 dark:text-warning-300">
                  Bulan: {filterBulan} <FiX className="h-3 w-3" />
                </button>
              )}
              <button onClick={clearFilters} className="text-xs font-medium text-danger-600 hover:text-danger-700 dark:text-danger-400">
                Reset semua
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <TableSkeleton rows={10} columns={5} />
        ) : (
          <DataTable
            columns={columns}
            rows={filteredList}
            rowKey={(p, i) => p.id || i}
            empty={
              <EmptyState
                icon={FiDollarSign}
                title="Belum ada pembayaran"
                description={hasActiveFilters ? "Tidak ada data yang cocok dengan filter." : "Transaksi pembayaran akan tampil di sini."}
              />
            }
          />
        )}
      </Card>
    </div>
  );
}
